import { createSelector, createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import {
  selectEnableAutoscalingForServices,
  selectFeatureObjectStorageEnabled
} from "Reducers/featureFlags/featureFlags.selectors";
import { gitProjectSelector } from "Reducers/project/project";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";

import type { Capabilities } from "@packages/client";
import type { RootState } from "Store/configureStore";

export const loadProjectCapability = createAppAsyncThunk(
  "app/project/capability/load",
  (
    {
      organizationName,
      projectId
    }: { organizationId: string; projectId: string; organizationName: string },
    { getState }
  ) => {
    // using organization name because that is what we actually use on the git project selector
    const project = gitProjectSelector(getState(), {
      organizationId: organizationName,
      projectId
    });

    return project?.getCapabilities();
  }
);

type CapabilitiesState = {
  data?: Record<string, Record<string, Capabilities | undefined> | undefined>;
  dataWithOrganizationId?: Record<
    string,
    Record<string, Capabilities | undefined> | undefined
  >;
  inflight?: Record<string, Record<string, boolean> | undefined>;
};

const initialState: CapabilitiesState = {};

const projectCapabilities = createSlice({
  name: "capabilities",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadProjectCapability.pending, (state, { meta }) => {
        const { projectId, organizationId } = meta.arg;
        setDeep(state, ["inflight", organizationId, projectId], true);
      })
      .addCase(loadProjectCapability.fulfilled, (state, { payload, meta }) => {
        const { projectId, organizationId, organizationName } = meta.arg;
        setDeep(state, ["inflight", organizationName, projectId], false);
        setDeep(state, ["data", organizationName, projectId], payload);
        setDeep(
          state,
          ["dataWithOrganizationId", organizationId, projectId],
          payload
        );
      })
      .addCase(loadProjectCapability.rejected, (state, { meta }) => {
        const { projectId, organizationId } = meta.arg;
        setDeep(state, ["inflight", organizationId, projectId], false);
      });
  }
});

export const projectCapabilitySelector = (
  state: RootState,
  { organizationId, projectId }: { organizationId: string; projectId: string }
) => state.capabilities?.data?.[organizationId]?.[projectId];

export const projectCapabilityWithOrganizationIdSelector = (
  state: RootState,
  { organizationId, projectId }: { organizationId: string; projectId: string }
) => state.capabilities?.dataWithOrganizationId?.[organizationId]?.[projectId];

export const selectProjectBuildResourcesEnabled = createSelector(
  projectCapabilitySelector,
  capabilities => capabilities?.build_resources?.enabled
);

export const selectCapabilitiesAreLoading = (
  state: RootState,
  { organizationId, projectId }: { organizationId: string; projectId: string }
) => state.capabilities?.inflight?.[organizationId]?.[projectId];

export const selectEnableCustomBackupSchedules = createSelector(
  projectCapabilitySelector,
  capabilities => capabilities?.data_retention?.enabled
);

export const selectEnableGuaranteedResources = createSelector(
  projectCapabilitySelector,
  capabilities => capabilities?.guaranteed_resources?.enabled
);

export const selectProjectTasksEnabled = createSelector(
  projectCapabilitySelector,
  capabilities => capabilities?.tasks?.enabled
);

export const supportsHorizontalScalingServicesSelector = createSelector(
  projectCapabilitySelector,
  selectEnableAutoscalingForServices,
  (capabilities, servicesAutoscalingEnabled) => {
    return Boolean(
      capabilities?.autoscaling?.supports_horizontal_scaling_services &&
      servicesAutoscalingEnabled
    );
  }
);
export const supportsObjectStorageSelector = createSelector(
  projectCapabilitySelector,
  selectFeatureObjectStorageEnabled,
  (capabilities, featureFlagEnabled) =>
    Boolean(capabilities?.object_storage?.enabled && featureFlagEnabled)
);

export const selectObjectStorageCapability = createSelector(
  projectCapabilitySelector,
  capabilities => capabilities?.object_storage
);

export default projectCapabilities.reducer;
